import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Badge, Modal, Form, Alert, Spinner, Table, InputGroup, FormControl } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faPlus, 
  faSearch, 
  faEdit, 
  faTrash, 
  faCheck, 
  faCopy, 
  faCog, 
  faTruck, 
  faExclamationTriangle, 
  faTimes,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Event, EventColor } from '../interfaces';
import axios from 'axios';
import moment from 'moment';
import EventForm from './EventForm';
import EventEquipmentManager from './EventEquipmentManager';
import EventPreparation from './EventPreparation';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import DefectReportModal from './DefectReportModal';
import EventEndChoiceModal from './EventEndChoiceModal';
import DefectReportGeneralModal from './DefectReportGeneralModal';
import { API_BASE_URL, buildApiUrl } from '../config/api';

interface EventListProps {
  onManageEquipment: (eventId: number) => void;
  onPrepareEvent: (eventId: number) => void;
  onShowDetails: (eventId: number) => void;
  onGoToCalendar: () => void;
}

const EventList: React.FC<EventListProps> = ({ onManageEquipment, onPrepareEvent, onShowDetails, onGoToCalendar }) => {
  const { user } = useAuth();
  const isTechnician = user?.role?.name === 'TECHNICIAN';
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [eventToEdit, setEventToEdit] = useState<Event | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterClient, setFilterClient] = useState<string>('');
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('-start_date');
  const [clients, setClients] = useState<any[]>([]); // Assuming Client and Location types are not directly imported here
  const [locations, setLocations] = useState<any[]>([]); // Assuming Client and Location types are not directly imported here
  
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');
  
  const [showDefectModal, setShowDefectModal] = useState<boolean>(false);
  const [showEventEndChoiceModal, setShowEventEndChoiceModal] = useState<boolean>(false);
  const [defectReportEventId, setDefectReportEventId] = useState<number | null>(null);
  const [defectDescription, setDefectDescription] = useState<string>('');
  const [isFinishingEvent, setIsFinishingEvent] = useState<boolean>(false);
  const [eventEquipment, setEventEquipment] = useState<any[]>([]);
  const [showGeneralDefectReportModal, setShowGeneralDefectReportModal] = useState<boolean>(false);
  
  // הצעה לעריכת ציוד
  const [showEquipmentEditSuggestion, setShowEquipmentEditSuggestion] = useState<boolean>(false);
  const [equipmentNeedsReview, setEquipmentNeedsReview] = useState<any[]>([]);
  
  // Modal לבחירת ציוד בשכפול
  // משתנים למודל בחירת ציוד בשכפול - הוסרו כי האירוע תמיד מועתק עם כל הציוד

  const STATUS_CHOICES = [
    { value: 'planned', label: 'מתוכנן', variant: 'primary' },
    { value: 'preparation', label: 'בהכנה', variant: 'warning' },
    { value: 'active', label: 'פעיל/בחוץ', variant: 'success' },
    { value: 'completed', label: 'הסתיים', variant: 'secondary' },
    { value: 'cancelled', label: 'בוטל', variant: 'danger' },
    { value: 'no_equipment_list', label: 'ללא רשימת ציוד', variant: 'light' },
  ];

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const clientsRes = await axios.get<any[]>(buildApiUrl('settings/clients/')); // Assuming Client type is not directly imported
        setClients(clientsRes.data);
        const locationsRes = await axios.get<any[]>(buildApiUrl('settings/locations/')); // Assuming Location type is not directly imported
        setLocations(locationsRes.data);
      } catch (err) {
        console.error("שגיאה בטעינת נתונים לפילטרים:", err);
      }
    };
    fetchFilterData();
  }, []);

  useEffect(() => {
    if (isTechnician) {
      setShowGeneralDefectReportModal(false);
    }
  }, [isTechnician]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {}; // Assuming QueryParams type is not directly imported
      if (searchQuery) params.search = searchQuery;
      if (filterStatus) params.status = filterStatus;
      if (filterClient) params.client = parseInt(filterClient);
      if (filterLocation) params.location = parseInt(filterLocation);
      if (sortBy) params.ordering = sortBy;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;

      const response = await axios.get<any>(buildApiUrl('events/events/'), { params });
      console.log('API Response:', response.data);
      // Django REST Framework מחזיר את התוצאות ב-results
      const eventsData = response.data.results || response.data;
      console.log('Events Data:', eventsData);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (err) {
      console.error("שגיאה בטעינת אירועים:", err);
      setError("לא ניתן לטעון אירועים. וודא ששרת Django פועל.");
      setEvents([]); // ודא ש-events הוא מערך ריק במקרה של שגיאה
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchEvents();
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery, filterStatus, filterClient, filterLocation, sortBy, filterStartDate, filterEndDate]);

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
      setMessageModalTitle(title);
      setMessageModalContent(message);
      setMessageModalVariant(variant);
      setShowMessageModal(true);
  };
  
  const handleAddClick = () => { setEventToEdit(null); setShowForm(true); };
  const handleEditClick = (event: Event) => {
    setEventToEdit(event);
    setShowForm(true);
  };

  // נקרא כשהטופס שומר אירוע (חדש או מעודכן) – ואז ננווט לעמוד הפרטים שלו
  const handleFormSuccess = (savedEvent: Event) => {
    setShowForm(false); 
    fetchEvents(); 
    const nameForMsg = savedEvent.event_name ? `"${savedEvent.event_name}"` : 'האירוע';
    showMessage('הצלחה', `פרטי ${nameForMsg} נשמרו בהצלחה!`, 'success');
    if (savedEvent.id) {
      onShowDetails(savedEvent.id);
    }
  };
  const handleFormCancel = () => { setShowForm(false); setEventToEdit(null); };

  const handleDeleteClick = (id: number) => {
    if (user?.role?.name === 'TECHNICIAN' || (user?.role?.name !== 'ADMIN' && user?.role?.name !== 'PRODUCTION')) {
      showMessage('אין הרשאה', 'אין לך הרשאה למחוק אירועים. רק מנהלים והרצה יכולים לבצע מחיקה.', 'danger');
      return;
    }
    setConfirmModalTitle('אישור מחיקה');
    setConfirmModalMessage('האם אתה בטוח שברצונך למחוק אירוע זה? פעולה זו בלתי הפיכה.');
    setConfirmAction(() => async () => {
      try {
        await axios.delete(`${buildApiUrl('events/events/')}${id}/`);
        const deleted = events.find(e => e.id === id);
        const nameForMsg = deleted?.event_name ? `"${deleted.event_name}"` : '';
        showMessage('הצלחה', nameForMsg ? `האירוע ${nameForMsg} נמחק בהצלחה!` : 'האירוע נמחק בהצלחה!', 'success');
        fetchEvents();
      } catch (err) {
        console.error("שגיאה במחיקת אירוע:", err);
        showMessage('שגיאה', 'שגיאה במחיקת האירוע. אנא נסה שוב.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };
  
  const canPrepareEquipment = (event: Event) => {
    if (!event.start_date) return true;
    
    const now = new Date();
    const eventDate = new Date(event.start_date);
    const oneWeekBefore = new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    return now >= oneWeekBefore;
  };

  const handleEndEventClick = async (event: Event) => {
    try {
      // בדיקה שלא ניתן לסיים אירוע לפני התאריך שלו
      const now = new Date();
      const eventDate = new Date(event.start_date);
      
      if (now < eventDate) {
        const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        showMessage('שגיאה', `לא ניתן לסיים אירוע לפני התאריך שלו. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן לסמן כבוטל או למחוק אותו.`, 'danger');
        return;
      }
      
      console.log('פתיחת מודל בחירה לאירוע:', event.id);
      setDefectReportEventId(event.id);
      setShowEventEndChoiceModal(true);
    } catch (err) {
      console.error("שגיאה בפתיחת מודל בחירה:", err);
      showMessage('שגיאה', 'שגיאה בפתיחת מודל בחירה. אנא נסה שוב.', 'danger');
    }
  };
  
  const handleEndWithoutDefects = async (completionNotes: string = '') => {
    // בדיקה נוספת שלא ניתן לסיים אירוע לפני התאריך שלו
    const event = events.find(e => e.id === defectReportEventId);
    if (event) {
      const now = new Date();
      const eventDate = new Date(event.start_date);
      
      if (now < eventDate) {
        const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        showMessage('שגיאה', `לא ניתן לסיים אירוע לפני התאריך שלו. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן לסמן כבוטל או למחוק אותו.`, 'danger');
        setShowEventEndChoiceModal(false);
        return;
      }
    }
    
    setShowEventEndChoiceModal(false);
    setLoading(true);
    try {
      const response = await axios.post(buildApiUrl(`events/events/${defectReportEventId}/end_event/`), { completion_notes: completionNotes });
      const ev = events.find(e => e.id === defectReportEventId);
      const endedName = ev?.event_name ? `"${ev.event_name}"` : 'האירוע';
      
      // בדיקה אם יש ציוד שצריך עריכה
      const equipmentNeedsReview = response.data.equipment_needs_review || [];
      
      if (equipmentNeedsReview.length > 0) {
        // הצגת הצעה לעריכת ציוד
        setShowEquipmentEditSuggestion(true);
        setEquipmentNeedsReview(equipmentNeedsReview);
      } else {
        showMessage('הצלחה', `${endedName} הסתיים בהצלחה! הציוד הוחזר למלאי.`, 'success');
      }
      
      setDefectReportEventId(null);
      fetchEvents();
    } catch (err) {
      console.error("שגיאה בסיום אירוע:", err);
      showMessage('שגיאה', 'שגיאה בסיום האירוע. אנא נסה שוב.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleEndWithDefects = () => {
    setShowEventEndChoiceModal(false);
    setShowDefectModal(true);
  };

  const handleDefectReportSubmit = async (defectData: any) => {
      // בדיקה נוספת שלא ניתן לסיים אירוע לפני התאריך שלו
      const event = events.find(e => e.id === defectReportEventId);
      if (event) {
        const now = new Date();
        const eventDate = new Date(event.start_date);
        
        if (now < eventDate) {
          const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          showMessage('שגיאה', `לא ניתן לסיים אירוע לפני התאריך שלו. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן לסמן כבוטל או למחוק אותו.`, 'danger');
          setShowDefectModal(false);
          return;
        }
      }
      
      setLoading(true);
      try {
          const response = await axios.post(buildApiUrl(`events/events/${defectReportEventId}/end_event/`), defectData);
          const ev = events.find(e => e.id === defectReportEventId);
          const endedName = ev?.event_name ? `"${ev.event_name}"` : 'האירוע';
          
          // בדיקה אם יש ציוד שצריך עריכה
          const equipmentNeedsReview = response.data.equipment_needs_review || [];
          
          if (equipmentNeedsReview.length > 0) {
            // הצגת הצעה לעריכת ציוד
            setShowEquipmentEditSuggestion(true);
            setEquipmentNeedsReview(equipmentNeedsReview);
          } else {
            showMessage('הצלחה', `${endedName} הסתיים בהצלחה! הציוד הוחזר למחסן.`, 'success');
          }
          
          /* סגירת מודל דיווח — אחרי הודעת הצלחה ב-DefectReportModal */
          setDefectDescription('');
          setEventEquipment([]);
          fetchEvents();
      } catch (err) {
          console.error("שגיאה בסיום אירוע:", err);
          showMessage('שגיאה', 'שגיאה בסיום האירוע. אנא נסה שוב.', 'danger');
      } finally {
          setLoading(false);
          setIsFinishingEvent(false);
      }
  };

  const handleGeneralDefectReport = async (_defectData: any) => {
    /* הודעת הצלחה מוצגת ב-DefectReportGeneralModal */
  };

  const handleDuplicateClick = (eventToDuplicate: Event) => {
    setConfirmModalTitle('אישור שכפול');
    setConfirmModalMessage('האם אתה בטוח שברצונך לשכפל אירוע זה?');
    setConfirmAction(() => async () => {
      try {
        // סגור את מודל האישור מיד עם תחילת הפעולה
        setShowConfirmModal(false);
        const response = await axios.post(buildApiUrl(`events/events/${eventToDuplicate.id}/duplicate/`));
        let newEvent = response.data;
        if (!newEvent || !newEvent.id) {
          throw new Error('תגובה לא תקינה מהשרת בשכפול האירוע');
        }
        
        // הצגת הודעת הצלחה עם פרטים על זמינות הציוד
        const unavailableCount = newEvent.equipment_availability?.unavailable_equipment?.length || 0;
        const availableCount = newEvent.equipment_availability?.available_equipment?.length || 0;
        
        let message = `האירוע שוכפל בהצלחה עם כל הציוד!\n\n`;
        message += `ציוד זמין: ${availableCount} פריטים\n`;
        
        if (unavailableCount > 0) {
          message += `ציוד לא זמין: ${unavailableCount} פריטים\n\n`;
          message += `הציוד הלא זמין:\n`;
          newEvent.equipment_availability.unavailable_equipment.forEach((item: any) => {
            message += `• ${item.equipment_name}: ${item.unavailable_quantity} יחידות לא זמינות (${item.reason})\n`;
          });
        } else {
          message += `כל הציוד זמין!`;
        }
        
        // הוספת הערות לאחר סיום אם קיימות
        if (newEvent.post_completion_notes_copied) {
          message += `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
          message += `⚠️ הערות שהוגדרו לאחר סיום האירוע המקורי הועברו להערות הכלליות של האירוע המשוכפל:`;
          message += `\n\n"${newEvent.post_completion_notes_copied}"`;
        }
        
        showMessage('הצלחה', message, 'success');
        
        // אם השרת מחזיר רק מזהה, משוך את פרטי האירוע המלאים
        if (!newEvent.event_name || !newEvent.start_date) {
          const fetched = await axios.get(buildApiUrl(`events/events/${newEvent.id}/`));
          newEvent = fetched.data;
        }
        setEventToEdit(newEvent);
        setShowForm(true);
        fetchEvents();
      } catch (err) {
        console.error("שגיאה בשכפול אירוע:", err);
        showMessage('שגיאה', 'שגיאה בשכפול האירוע. אנא נסה שוב.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  // פונקציות לטיפול בבחירת ציוד בשכפול - הוסרו כי האירוע תמיד מועתק עם כל הציוד
  
  const handleSort = (field: string) => {
      if (sortBy.replace('-', '') === field) {
          setSortBy(sortBy.startsWith('-') ? field : `-${field}`);
      } else {
          setSortBy(field);
      }
  };
  
  const getSortIndicator = (field: string) => {
      if (sortBy === field) {
          return ' 🔼';
      }
      if (sortBy === `-${field}`) {
          return ' 🔽';
      }
      return null;
  };

  const getStatusVariant = (status: string) => {
    return STATUS_CHOICES.find(choice => choice.value === status)?.variant || 'secondary';
  };

  const getStatusStyle = (status: string) => {
    if (status === 'no_equipment_list') {
      return { backgroundColor: '#ffcc80', color: '#000' } as React.CSSProperties;
    }
    return {} as React.CSSProperties;
  };
  
  const handleClientNameClick = (clientId: number | undefined) => {
    if (clientId) {
      setFilterClient(String(clientId));
    }
  };

  const handleLocationNameClick = (locationId: number | null | undefined) => {
      if (locationId) {
        setFilterLocation(String(locationId));
      }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>רשימת אירועים</h2>
        <div>
          <Button variant="info" onClick={onGoToCalendar} className="me-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
            לוח שנה
          </Button>
          <Button
            variant="warning"
            onClick={() => setShowGeneralDefectReportModal(true)}
            className="me-2"
            disabled={isTechnician}
            title={isTechnician ? 'אין לטכנאי הרשאה לדווח תקלות; רק ראש צוות ומעלה' : ''}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            דיווח על ציוד תקול
          </Button>
          <Button variant="success" onClick={handleAddClick} disabled={user?.role?.name === 'TECHNICIAN' || user?.role?.name === 'TEAM_LEADER'} title={user?.role?.name === 'TECHNICIAN' || user?.role?.name === 'TEAM_LEADER' ? 'אין לך הרשאה להוסיף אירועים' : ''}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            הוסף אירוע חדש
          </Button>
        </div>
      </div>

      <Form className="mb-4 p-3 border rounded bg-light">
        <Row>
          <Col md={3}>
            <Form.Group controlId="searchQuery">
              <Form.Label>חיפוש</Form.Label>
              <Form.Control
                type="text"
                placeholder="חפש לפי שם אירוע, תיאור..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="filterStatus">
              <Form.Label>סטטוס</Form.Label>
              <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">הכל</option>
                {STATUS_CHOICES.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="filterClient">
              <Form.Label>לקוח</Form.Label>
              <Form.Select value={filterClient} onChange={(e) => setFilterClient(e.target.value)}>
                <option value="">הכל</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.client_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="filterLocation">
              <Form.Label>מיקום</Form.Label>
              <Form.Select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}>
                <option value="">הכל</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.location_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mt-3">
            <Col md={6}>
                <Form.Group controlId="filterStartDate">
                    <Form.Label>מתאריך (פורמט: YYYY-MM-DD)</Form.Label>
                    <Form.Control type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group controlId="filterEndDate">
                    <Form.Label>עד תאריך (פורמט: YYYY-MM-DD)</Form.Label>
                    <Form.Control type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
                </Form.Group>
            </Col>
        </Row>
      </Form>

      {loading && <Spinner animation="border" role="status"><span className="visually-hidden">טוען אירועים...</span></Spinner>}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        !events || events.length === 0 ? (
          <p>אין אירועים במערכת. הוסף אירוע חדש כדי להתחיל, או שנה את מסנני החיפוש.</p>
        ) : (
          <Table striped bordered hover responsive className="text-right">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}># {getSortIndicator('id')}</th>
                  <th onClick={() => handleSort('event_name')} style={{ cursor: 'pointer' }}>שם אירוע {getSortIndicator('event_name')}</th>
                  <th>לקוח</th>
                  <th onClick={() => handleSort('start_date')} style={{ cursor: 'pointer' }}>תאריך התחלה {getSortIndicator('start_date')}</th>
                  <th onClick={() => handleSort('end_date')} style={{ cursor: 'pointer' }}>תאריך סיום {getSortIndicator('end_date')}</th>
                  <th>מיקום</th>
                  <th>סטטוס</th>
                  <th>צבע</th>
                  <th>אירוע אב</th>
                  <th>פעולות</th>
                </tr>
              </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id} onClick={() => onShowDetails(event.id!)} style={{ cursor: 'pointer' }}>
                  <td>{event.id}</td>
                  <td>{event.event_name}</td>
                  <td>
                      {event.client_name ? (
                          <span onClick={(e) => { e.stopPropagation(); handleClientNameClick(event.client); }} style={{cursor: 'pointer', textDecoration: 'underline'}}>
                              {event.client_name}
                          </span>
                      ) : (
                          event.free_text_client || 'N/A'
                      )}
                  </td>
                  <td>{new Date(event.start_date).toLocaleString('he-IL')}</td>
                  <td>{new Date(event.end_date).toLocaleString('he-IL')}</td>
                  <td>
                      {event.location_name ? (
                          <span onClick={(e) => { e.stopPropagation(); handleLocationNameClick(event.location); }} style={{cursor: 'pointer', textDecoration: 'underline'}}>
                              {event.location_name}
                          </span>
                      ) : (
                          event.free_text_location || 'N/A'
                      )}
                  </td>
                  <td>
                    {event.status === 'no_equipment_list' ? (
                      <span className="badge" style={{ backgroundColor: '#f8bbd0', color: '#000' }}>
                        {STATUS_CHOICES.find(choice => choice.value === event.status)?.label || event.status}
                      </span>
                    ) : (
                      <Badge bg={getStatusVariant(event.status)}>
                        {STATUS_CHOICES.find(choice => choice.value === event.status)?.label || event.status}
                      </Badge>
                    )}
                  </td>
                  <td>
                    {event.color_hex && event.status === 'preparation' && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: event.color_hex,
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            marginBottom: '3px'
                          }}
                          title={event.color_name}
                        ></div>
                        {event.color_name && <small>{event.color_name}</small>}
                      </div>
                    )}
                  </td>
                  <td>{event.parent_event_name || 'N/A'}</td>
                  <td>
                    <Button variant="primary" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); onManageEquipment(event.id); }} disabled={user?.role?.name === 'TECHNICIAN'} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לניהול ציוד' : ''}>
                      <FontAwesomeIcon icon={faCog} className="me-1" />
                      ניהול ציוד
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      className="me-2" 
                      onClick={(e) => { e.stopPropagation(); onPrepareEvent(event.id); }}
                      disabled={user?.role?.name === 'TECHNICIAN' || !canPrepareEquipment(event)}
                      title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה להכנת/העמסת אירועים' : (!canPrepareEquipment(event) ? 'לא ניתן להכין ציוד לפני השבוע שלפני התאריך' : '')}
                      style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' }}
                    >
                      <FontAwesomeIcon icon={faTruck} className="me-1" />
                      הכנה / העמסה
                    </Button>
                    <Button variant="warning" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleEditClick(event); }} disabled={user?.role?.name === 'TECHNICIAN'} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לעריכת אירועים' : ''}>
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      ערוך
                    </Button>
                    {event.status !== 'completed' && event.status !== 'cancelled' && (
                        <Button 
                            variant="success" 
                            size="sm" 
                            className="me-2" 
                            onClick={(e) => { e.stopPropagation(); handleEndEventClick(event); }} 
                            disabled={user?.role?.name === 'TECHNICIAN' || isFinishingEvent || (new Date() < new Date(event.start_date))}
                            title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לסיים אירועים' : (new Date() < new Date(event.start_date) ? 'לא ניתן לסיים אירוע לפני התאריך שלו' : '')}
                        >
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            סיים
                        </Button>
                    )}
                    <Button variant="danger" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleDeleteClick(event.id); }} disabled={user?.role?.name === 'TECHNICIAN' || (user?.role?.name !== 'ADMIN' && user?.role?.name !== 'PRODUCTION')} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה למחוק אירוע' : (user?.role?.name !== 'ADMIN' && user?.role?.name !== 'PRODUCTION' ? 'רק מנהלים והרצה יכולים למחוק אירועים' : '')}>
                      <FontAwesomeIcon icon={faTrash} className="me-1" />
                      מחק
                    </Button>
                    <Button variant="info" size="sm" onClick={(e) => { e.stopPropagation(); handleDuplicateClick(event); }} disabled={user?.role?.name === 'TECHNICIAN'} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לשכפל אירועים' : ''}>
                      <FontAwesomeIcon icon={faCopy} className="me-1" />
                      שכפל
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      )}

      <Modal show={showForm} onHide={handleFormCancel} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{eventToEdit ? 'עריכת פרטי אירוע' : 'הוספת אירוע חדש'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EventForm
            eventToEdit={eventToEdit}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </Modal.Body>
      </Modal>

      <EventEndChoiceModal
          show={showEventEndChoiceModal}
          onHide={() => setShowEventEndChoiceModal(false)}
          eventName={events.find(e => e.id === defectReportEventId)?.event_name || ''}
          onEndWithoutDefects={handleEndWithoutDefects}
          onEndWithDefects={handleEndWithDefects}
      />

      <DefectReportModal
          show={showDefectModal}
          onHide={() => {
            setShowDefectModal(false);
            setDefectReportEventId(null);
          }}
          eventId={defectReportEventId || 0}
          eventName={events.find(e => e.id === defectReportEventId)?.event_name || ''}
          onDefectReport={handleDefectReportSubmit}
      />

      {/* Modal לבחירת ציוד בשכפול - הוסר כי האירוע תמיד מועתק עם כל הציוד */}

      {/* מודל הצעה לעריכת ציוד */}
      <Modal show={showEquipmentEditSuggestion} onHide={() => setShowEquipmentEditSuggestion(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            הצעה לעריכת רשימת הציוד
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            זוהה ציוד שנוסף או נערך לאחר תאריך האירוע. האם תרצה לערוך את רשימת הציוד?
          </Alert>
          
          <div className="mb-3">
            <h6>ציוד שזוהה כערוך לאחר תאריך האירוע:</h6>
            <div className="list-group">
              {equipmentNeedsReview.map((item, index) => (
                <div key={index} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{item.equipment_name}</strong>
                      <br />
                      <small className="text-muted">
                        כמות נדרשת: {item.quantity_needed}
                      </small>
                    </div>
                    <div className="text-end">
                      <small className="text-muted">
                        נערך ב: {new Date(item.last_modified).toLocaleDateString('he-IL')}
                        <br />
                        תאריך סיום אירוע: {new Date(item.event_end_date).toLocaleDateString('he-IL')}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="d-flex gap-2">
            <Button 
              variant="primary" 
              onClick={() => {
                setShowEquipmentEditSuggestion(false);
                // פתיחת דף עריכת האירוע
                window.location.href = `/events/edit/${defectReportEventId}`;
              }}
            >
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              ערוך רשימת ציוד
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowEquipmentEditSuggestion(false);
                showMessage('הצלחה', 'האירוע הסתיים בהצלחה! דיווח התקלות נשמר.', 'success');
              }}
            >
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              המשך ללא עריכה
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <MessageModal
        show={showMessageModal}
        onHide={() => setShowMessageModal(false)}
        title={messageModalTitle}
        message={messageModalContent}
        variant={messageModalVariant}
      />

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        title={confirmModalTitle}
        message={confirmModalMessage}
        onConfirm={confirmAction || (() => {})}
      />

      <DefectReportGeneralModal
        show={showGeneralDefectReportModal && !isTechnician}
        onHide={() => setShowGeneralDefectReportModal(false)}
        onDefectReport={handleGeneralDefectReport}
      />
    </div>
  );
};

export default EventList;
